import { Button, ListPage, RefreshButton } from '@skylabs-digital/backoffice-kit';
import { useApiClient, useSnackbar } from '@skylabs-digital/react-proto-kit';
import { RefreshCw } from 'lucide-react';
import { useCallback, useState } from 'react';

import { appsApi } from '../api/apps.api';
import { unwrap } from '../api/unwrap';
import { AppList } from '../components/apps/AppList';

interface SyncResult {
  created: number;
  updated: number;
  orphaned: number;
  reactivated: number;
  total: number;
}

export function AppsPage() {
  const { connector } = useApiClient();
  const { showSnackbar } = useSnackbar();
  const [syncing, setSyncing] = useState(false);

  const { data: apps, loading, error, refetch } = appsApi.useList();
  const { mutate: deleteApp } = appsApi.useDelete();

  const handleDelete = useCallback(
    async (id: string) => {
      await deleteApp(id);
      await refetch();
    },
    [deleteApp, refetch],
  );

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await connector.post<SyncResult>('apps/sync', {});
      const d = unwrap(result);
      const parts: string[] = [];
      if (d.created) parts.push(`${d.created} created`);
      if (d.updated) parts.push(`${d.updated} updated`);
      if (d.orphaned) parts.push(`${d.orphaned} orphaned`);
      if (d.reactivated) parts.push(`${d.reactivated} reactivated`);
      const msg =
        parts.length > 0
          ? `Sync complete: ${parts.join(', ')} (${d.total} apps in IdAchu)`
          : `Sync complete — ${d.total} apps, no changes`;
      showSnackbar({ message: msg, variant: 'success' });
      await refetch();
    } catch {
      showSnackbar({ message: 'Failed to sync apps from IdAchu', variant: 'error' });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <ListPage
      title="Apps"
      description="Apps configured in this tenant. Sync pulls them from IdAchu."
      actions={
        <div className="flex items-center gap-2">
          <RefreshButton onRefresh={refetch} isRefreshing={loading} />
          <Button onClick={handleSync} disabled={syncing}>
            <RefreshCw className={syncing ? 'animate-spin' : undefined} />
            {syncing ? 'Syncing…' : 'Sync from IdAchu'}
          </Button>
        </div>
      }
    >
      <AppList
        apps={apps ?? []}
        loading={loading}
        error={error as Error | null | undefined}
        onDelete={handleDelete}
      />
    </ListPage>
  );
}
